/**
 * Menu scan API: upload file (async) and poll status/result.
 * Contract matches backend: scanId, status, message, dishes (partial while processing).
 */

import okaoAxios from '../helpers/axios'

export const MENU_SCAN_STATUS = {
  PENDING: 'PENDING',
  OCR_DONE: 'OCR_DONE',
  EXTRACTION_DONE: 'EXTRACTION_DONE',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
} as const

export type MenuScanStatus = (typeof MENU_SCAN_STATUS)[keyof typeof MENU_SCAN_STATUS]

export interface MenuScanDish {
  name: string
  description: string | null
  price: number | string | null
  section: string | null
}

export interface MenuScanResult {
  scanId: string
  restaurantId: string | null
  status: MenuScanStatus | string
  message: string | null
  dishes: MenuScanDish[]
  summary?: MenuScanSummaryResponse
}

const BASE = '/menus/scan'

export async function uploadMenuScan(
  file: File,
  restaurantId?: string | null
): Promise<MenuScanResult> {
  const formData = new FormData()
  formData.append('file', file)
  if (restaurantId != null && restaurantId !== '') {
    formData.append('restaurantId', restaurantId)
  }
  const { data } = await okaoAxios.post<MenuScanResult>(`${BASE}/upload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 60000,
  })
  return data
}

export async function getMenuScanStatus(scanId: string): Promise<MenuScanResult> {
  const { data } = await okaoAxios.get<MenuScanResult>(`${BASE}/${scanId}`, {
    timeout: 15000,
  })
  return data
}

export interface MenuScanSummaryDishItem {
  name: string
  description?: string | null
  price?: number | null
  section?: string | null
}

export interface MenuScanSummaryRequest {
  scanId?: string | null
  restaurantName?: string | null
  dishes: MenuScanSummaryDishItem[]
  userContext?: string | null
}

export interface MenuScanSummaryResponse {
  quickTake: string
  goodFor: string
  watchOut: string
  friendPick: string
}

const SUMMARY_TIMEOUT_MS = 10000

export async function getMenuScanSummary(
  payload: MenuScanSummaryRequest
): Promise<MenuScanSummaryResponse> {
  const { data } = await okaoAxios.post<MenuScanSummaryResponse>(
    `${BASE}/summary`,
    payload,
    { timeout: SUMMARY_TIMEOUT_MS }
  )
  return data
}

export interface MenuSaveRequest {
  restaurantId?: string | null
  menuName?: string | null
  dishes?: MenuScanDish[]
}

export interface MenuSaveResponse {
  scanId: string
  menuId: string | null
  menuName: string | null
  dishesSaved: number
  newDishesAddedCount: number
  dedupedCount: number
  createdUserProvidedMenu: boolean
  pointsGranted: number
  pointsReason: string
  published: boolean
  publishMessage: string | null
  message: string
}

export async function saveMenuFromScan(
  scanId: string,
  payload: MenuSaveRequest
): Promise<MenuSaveResponse> {
  const { data } = await okaoAxios.post<MenuSaveResponse>(
    `${BASE}/${scanId}/save`,
    {
      restaurantId: payload.restaurantId ?? undefined,
      menuName: payload.menuName ?? undefined,
      dishes: payload.dishes ?? [],
    },
    { timeout: 15000 }
  )
  return data
}

export enum SavedMenuType {
  RESTAURANT_MENU = 'RESTAURANT_MENU',
  SCAN_ONLY = 'SCAN_ONLY',
}

export interface SavedMenuItem {
  savedId: string
  type: SavedMenuType
  savedAt: string
  menuId: string | null
  menuName: string
  restaurantId: string | null
  restaurantName: string | null
  restaurantAlias: string | null
  dishCount: number
  sampleDishes: string[]
  hasImage: boolean
  imageUrl: string | null
}

export interface SavedMenusResponse {
  items: SavedMenuItem[]
  page: number
  size: number
  totalElements: number
  totalPages: number
}

export interface GetSavedMenusParams {
  page?: number
  size?: number
  sort?: 'recent' | 'name'
}

export interface SavedMenuDetailDish {
  dishId: string | null
  name: string
  description: string | null
  price: string | null
  section: string | null
  imageUrl: string | null
}

export interface SavedMenuDetail {
  savedId: string
  type: 'SCAN_ONLY' | 'RESTAURANT_MENU'
  menuId: string | null
  scanId: string | null
  menuName: string
  restaurantId: string | null
  restaurantName: string | null
  restaurantAlias: string | null
  savedAt: string
  imageUrl: string | null
  dishes: SavedMenuDetailDish[]
  summary: MenuScanSummaryResponse | null
}

export async function getSavedMenus(
  params: GetSavedMenusParams = {}
): Promise<SavedMenusResponse> {
  const { page = 0, size = 20, sort = 'recent' } = params
  const { data } = await okaoAxios.get<SavedMenusResponse>('/menus/saved', {
    params: { page, size, sort },
    timeout: 15000,
  })
  return data
}

export async function getSavedMenuDetail(savedId: string): Promise<SavedMenuDetail> {
  const { data } = await okaoAxios.get<SavedMenuDetail>(
    `/menus/scan/saved/${savedId}/detail`,
    { timeout: 15000 }
  )
  return data
}
